/**
 * The default language used in the application.
 * @type {string}
 */
export const DEFAULT_LANG = 'en'
/**
 * The default bank constant.
 * @type {string}
 */
export const DEFAULT_BANK = 'nexi'

/**
 * An object mapping bank identifiers to their corresponding theme names.
 * Note: 'isp' uses the 'nexi' theme but has a different logo.
 *
 * @constant
 * @type {Object.<string, string>}
 * @property {string} nexi - Nexi bank theme.
 * @property {string} uci - UCI bank theme.
 * @property {string} isp - ISP bank uses Nexi theme with a different logo.
 */
// only list banks that can change theme and/or logo, for the rest it already falls back to nexi (e.g. pagopa)
export const BANKS_FOR_THEME = {
  nexi: 'nexi',
  ['unicredit-e-platform']: 'uci',
  isp: 'nexi', // isp uses nexi theme but different logo
}

export const TENANT_FOR_THEME = {
  nexi: 'nexi',
  isp: 'isp',
  uci: 'unicredit-e-platform',
  nexid: 'nexi'
}

export const TENANT_BY_THEME = Object.entries(TENANT_FOR_THEME).reduce((acc, [tenant, theme]) => {
  acc[theme] = tenant
  return acc
}, {})

export const PROVIDERID_FOR_THEME = {
  nexi: 'nb',
  isp: 'nb',
  uci: 'ub'
}

export const ELIGIBLE_TENANTS = ['nexi', 'isp', 'uci']

/**
 * Array of eligible roles.
 * @type {string[]}
 */
export const ELIGIBLE_ROLES = ['admin', 'operator', 'reporter', 'NBadmin', 'NBoperator', 'NBreporter']

export const ELIGIBLE_ROLES_USERS = ['admin', 'operator', 'reporter']
/**
 * Array of vas status.
 * @type {string[]}
 */
export const VAS_STATUSES = ['Enabled', 'Disabled']
/**
 * Array of user status.
 * @type {string[]}
 */
export const USER_STATUSES = ['ACTIVATED', 'DEACTIVATED']
/**
 * Array of supported languages.
 * @type {string[]}
 */
export const SUPPORTED_LANGUAGES = ['bs', 'cs', 'de', 'el','en', 'hr', 'hu', 'it', 'pl', 'ro', 'sk', 'sl', 'sq', 'sr'] // maintain alphabetical sorting

/**
 * Object of supported languages in ISO639 2 code.
 * @type {Object.<string, string>}
 */
export const LANGUAGES_ISO639_2_CODE_FORMAT = {
  ar: 'ARA',
  bs: 'BOS',
  cs: 'CZE',
  de: 'DEU',
  el: 'ELL',
  en: 'ENG',
  es: 'SPA',
  fr: 'FRA',
  hr: 'HRV',
  hu: 'HUN',
  it: 'ITA',
  ja: 'JPN',
  pl: 'POL',
  pt: 'POR',
  ro: 'RUM',
  ru: 'RUS',
  sk: 'SLK',
  sl: 'SLV',
  sq: 'ALB',
  sr: 'SRP',
  zh: 'ZHO'
}

/**
 * Array of supported plan type.
 * @type {string[]}
 */
export const SUBSCRIPTION_PLAN_TYPES = ['FIXED', 'VARIABLE']

/**
 * Array of supported plan statuses.
 * @type {string[]}
 */
export const SUBSCRIPTION_PLAN_STATUSES = ['ACTIVE', 'INACTIVE', 'COMPLETED']

/**
 * Array of supported rule type.
 * @type {string[]}
 */
export const PROCESSING_RULES_TYPES = [
  'CARD_BIN_BLOCK',
  'SINGLE_PAN_BLOCK',
  'MASKED_PAN',
  'CARD_TYPE',
  'IP_BLOCK',
  'CARDHOLDER_BLOCK',
  'SETTING_AUTO_LOCKOUT_TIMES',
  'SETTING_DUPLICATE_LOCKOUT_TIMES'
]

/**
 * Array of supported rule status.
 * @type {string[]}
 */

export const PROCESSING_RULES_STATUS = ['ENABLED', 'DISABLED']

/**
 * Array of supported card type.
 * @type {string[]}
 */
export const CARD_TYPE = ['CREDIT', 'DEBIT', 'PREPAID']

export const UNAUTH_ROUTES = ['recoverycredentials', 'reset-password', 'authorize', 'changepassword']

export const MULTI_MERCHANT_ALLOWED_ROLES = [
  'admin; admin_holding',
  'admin; admin_vat',
  'admin; admin_third_party',
  'admin; admin_acquirer',
  'admin; admin_tenant',
  'admin; admin_gateway',
  'admin; admin_multi_acquirer'
]

export const ROUTES_AND_PERMISSIONS = {
  // Dashboard: true,
  OperationManagement: 'operationsManagement',
  OrderManagement: 'orders',
  SubscriptionPlan: 'recurring',
  CreatePayByLinkPlus: 'services.paybylink',
  Moto: 'services.moto',
  // Profile: true,
  Reports: 'reports',
  CardCheck: 'settings.terminals.cardCheck',
  TerminalsManagement: 'settings.terminals',
  Users: 'settings.users',
  BookingManagement: 'booking.management',
  PblManagement: 'services.paybylink',
  PointsOfSales: 'settings.terminals',
  PayToCard: 'settings.terminals.vas.payToCard',
}

export const RESERVATION_TYPE = {
  PREPAID_REFUNDABLE: 'PREPAID_REFUNDABLE',
  PREPAID_NON_REFUNDABLE: 'PREPAID_NON_REFUNDABLE',
  GUARANTEED_NOSHOW: 'GUARANTEED_NOSHOW',
  GUARANTEED_PENALTY: 'GUARANTEED_PENALTY',
  BUNDLE: 'BUNDLE'
}

export const RESERVATION_CANCELLATION_POLICY_TYPE = {
  TOTAL_REFUND: 'TOTAL_REFUND',
  PARTIAL_REFUND: 'PARTIAL_REFUND',
  PENALTY_NOSHOW: 'PENALTY_NOSHOW',
  PENALTY: 'PENALTY' // currently not in use
}

export const VAS_FEATURE_NAMES = ['TRA', 'DynamicCurrencyConversion', 'Disputeless', 'PosTokenization', 'ClickNCollect']

export const RSQL_OPERATOR_MAP = {
  '>': '>',
  '<': '<',
  '>=': '>=',
  '<=': '<=',
  '==': '==',
  '!=': '!=',
  like: '=like=',
  ilike: '=ilike=',
  icase: '=icase=',
  notlike: '=notlike=',
  inotlike: '=inotlike=',
  ke: '=ke=',
  ik: '=ik=',
  nk: '=nk=',
  ni: '=ni=',
  ic: '=ic=',
  in: '=in=',
  out: '=out=',
  bt: '=bt=',
  nb: '=nb=',
  gt: '=gt=',
  lt: '=lt=',
  ge: '=ge=',
  le: '=le=',
  isnull: '=isnull=',
  null: '=null=',
  na: '=na=',
  nn: '=nn=',
  notnull: '=notnull=',
  isnotnull: '=isnotnull=',
  ai: '=ai='
}
